import Computer from "@mui/icons-material/Computer";
import Description from "@mui/icons-material/Description";
import Edit from "@mui/icons-material/Edit";
import Email from "@mui/icons-material/Email";
import Language from "@mui/icons-material/Language";
import MoreVert from "@mui/icons-material/MoreVert";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Person from "@mui/icons-material/Person";
import Refresh from "@mui/icons-material/Refresh";
import {
  Box,
  IconButton,
  Link as MuiLink,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { ManifestDocumentsQuery } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  DocumentReviewStatus,
  FILTERABLE_DOCUMENT_REVIEW_STATUSES,
  getDocumentReviewStatus,
  getDocumentReviewStatusLabel,
  getDocumentReviewStatusLabelFromRow,
} from "util/documentReviewStatus";

import { ManifestReviewDialog } from "../../Customer/Waste/Manifests/ManifestReviewDialog";
import { REQUEUEABLE_FAILED_STATUSES } from "../Jobs/utils";
import { MANIFEST_DOCUMENTS_QUERY } from "./api";
import {
  buildManifestDocumentsSearch,
  ManifestSourceBucket,
  MANIFEST_SOURCE_BUCKETS,
  MANIFEST_SOURCE_LABELS,
  manifestSourceBucket,
  manifestSourceLabel,
  reparseMenuLabel,
} from "./helpers";
import { ReParseDialog } from "./ReParseDialog";

type Row = ManifestDocumentsQuery["documents"]["items"][number];

const REVIEW_STATUS_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  FILTERABLE_DOCUMENT_REVIEW_STATUSES.map((status) => [
    status,
    getDocumentReviewStatusLabel(status),
  ])
);

// One icon per provenance bucket for the Source column. Tooltip carries the
// human label so the column can stay narrow.
const SOURCE_ICON_BY_BUCKET: Record<ManifestSourceBucket, typeof Computer> = {
  api: Computer,
  public: Language,
  upload: Person,
  email: Email,
  csv: Description,
};

// Deep-links into the staff Jobs page pre-filtered to manifest ingest tasks
// that hit an investigable failure state. The status list is sourced from
// REQUEUEABLE_FAILED_STATUSES so this link can't drift from the Jobs page.
const MANIFEST_FAILED_JOBS_SEARCH =
  "task:PROCESS_MANIFESTS,DOCUMENT_AI_ENRICHMENT " +
  `status:${REQUEUEABLE_FAILED_STATUSES.join(",")}`;
const MANIFEST_FAILED_JOBS_LINK = `/staff/jobs?omnisearch=${encodeURIComponent(
  MANIFEST_FAILED_JOBS_SEARCH
)}`;

/**
 * Staff-only observability page that lists every RcraManifest document across
 * all tenants alongside the manifest record it ended up linked to (if any).
 * Helps identify orphaned documents, documents that landed on the wrong
 * manifest, and where each manifest was ingested from. Optionally pre-scoped to
 * one tenant via a `tenantId` query param (deep link from an org page).
 */
export function ManifestDocuments() {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const seedTenantId = searchParams.get("tenantId");

  // Row to re-parse. Null when the dialog is closed. The dialog itself owns
  // the override field state; this is just the open/closed signal + which row.
  const [pendingReParseRow, setPendingReParseRow] = useState<Row | null>(null);
  // Documents to open in the manifest-specific review dialog. Used both for
  // direct edits (Edit action) and for the post-re-parse review handoff —
  // the analyzing dialog otherwise falls back to the generic Document AI flow.
  const [reviewDocumentIds, setReviewDocumentIds] = useState<string[] | null>(null);
  // Per-row overflow menu anchor (Re-parse lives here as a secondary action).
  // Two states instead of an object so each updates with one setter call.
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [actionsAnchorRow, setActionsAnchorRow] = useState<Row | null>(null);
  const closeActionsMenu = useCallback(() => {
    setActionsAnchorEl(null);
    setActionsAnchorRow(null);
  }, []);

  const columns = useMemo<OmnisearchGridColDef<Row>[]>(
    () => [
      {
        field: "title",
        headerName: "Title",
        flex: 2,
        minWidth: 200,
        renderCell: ({ row }) =>
          row.tenantId ? (
            <MuiLink
              // Also seed the destination documents grid's omnisearch so it
              // filters to this document, not just selects it in the list.
              href={`/o/${row.tenantId}/documents/${row.id}?omnisearch=${encodeURIComponent(
                `id:${row.id}`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              <Typography variant="body2" noWrap>
                {row.title}
              </Typography>
              <OpenInNew fontSize="inherit" />
            </MuiLink>
          ) : (
            <Typography variant="body2">{row.title}</Typography>
          ),
      },
      {
        field: "tenant",
        headerName: "Tenant",
        flex: 1,
        minWidth: 160,
        sortable: false,
        // multipleTenant renders MultipleTenantPicker in the filter popover so
        // staff can pick tenants from a searchable dropdown. The picker
        // serializes the selection as `tenantId:uuid1|uuid2`; the resolver's
        // `tenantId` omnisearch key resolves that to `tenantId: { in: [...] }`.
        filterKeyType: "multipleTenant",
        valueGetter: (_, row) => row.tenant?.name ?? "(no tenant)",
        renderCell: ({ row }) => (
          <Typography variant="body2" noWrap>
            {row.tenant?.name ?? "(no tenant)"}
          </Typography>
        ),
      },
      {
        field: "documentSource",
        headerName: "Source",
        flex: 0.6,
        minWidth: 130,
        sortable: false,
        filterKeyType: "enumAutocomplete",
        enumValues: [...MANIFEST_SOURCE_BUCKETS],
        enumPresentationFunction: manifestSourceLabel,
        renderCell: ({ row }) => {
          const bucket = manifestSourceBucket(row);
          const Icon = SOURCE_ICON_BY_BUCKET[bucket];
          const label = MANIFEST_SOURCE_LABELS[bucket];
          return (
            <Tooltip title={label}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Icon fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography variant="body2" noWrap>
                  {label}
                </Typography>
              </Stack>
            </Tooltip>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Uploaded",
        flex: 0.7,
        minWidth: 130,
        valueGetter: (_, row) => row.createdAt,
        renderCell: ({ row }) => (row.createdAt ? getUtcDisplayDate(row.createdAt, "med") : "—"),
      },
      {
        field: "reviewStatus",
        headerName: "Review Status",
        flex: 0.7,
        minWidth: 140,
        filterKeyType: "enumAutocomplete",
        enumValues: FILTERABLE_DOCUMENT_REVIEW_STATUSES,
        enumPresentationFunction: (enumValue) =>
          REVIEW_STATUS_LABEL_BY_VALUE[enumValue] ?? enumValue,
        valueGetter: (_, row) => getDocumentReviewStatusLabelFromRow(row),
        renderCell: ({ row }) => {
          const label = getDocumentReviewStatusLabelFromRow(row);
          const errorTitle =
            getDocumentReviewStatus(row) === DocumentReviewStatus.Failed
              ? (row.extractionError ?? "")
              : "";
          return (
            <Tooltip title={errorTitle}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" noWrap>
                  {label}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "lastExtractionAt",
        headerName: "Last parsed",
        flex: 0.7,
        minWidth: 130,
        filterKeyType: "date",
        valueGetter: (_, row) => row.lastExtractionAt,
        renderCell: ({ row }) =>
          row.lastExtractionAt ? getUtcDisplayDate(row.lastExtractionAt, "med") : "—",
      },
      {
        field: "trackingNumber",
        headerName: "Tracking #",
        flex: 0.8,
        minWidth: 150,
        sortable: false,
        valueGetter: (_, row) => row.sourceWasteManifest?.manifestTrackingNumber ?? "",
        renderCell: ({ row }) => {
          const mtn = row.sourceWasteManifest?.manifestTrackingNumber;
          return mtn ? (
            <Typography variant="body2">{mtn}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        field: "extractedGeneratorId",
        headerName: "Generator ID",
        flex: 0.7,
        minWidth: 140,
        sortable: false,
        valueGetter: (_, row) => row.sourceWasteManifest?.generatorId ?? "",
        renderCell: ({ row }) => {
          const id = row.sourceWasteManifest?.generatorId;
          return id ? (
            <Typography variant="body2">{id}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        field: "shippedDate",
        headerName: "Shipped Date",
        flex: 0.7,
        minWidth: 120,
        sortable: false,
        valueGetter: (_, row) => row.sourceWasteManifest?.shippedDate ?? "",
        renderCell: ({ row }) =>
          row.sourceWasteManifest?.shippedDate
            ? getUtcDisplayDate(row.sourceWasteManifest.shippedDate, "med")
            : "—",
      },
      {
        field: "linkedFacility",
        headerName: "Linked facility",
        flex: 0.9,
        minWidth: 160,
        sortable: false,
        valueGetter: (_, row) => row.sourceProcessedManifest?.facility?.name ?? "",
        renderCell: ({ row }) => {
          const facility = row.sourceProcessedManifest?.facility;
          return facility ? (
            <Typography variant="body2" noWrap>
              {facility.name}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        field: "linkedManifest",
        headerName: "Linked manifest",
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (_, row) => row.sourceProcessedManifest?.manifestTrackingNumber ?? "",
        renderCell: ({ row }) => {
          const linked = row.sourceProcessedManifest;
          if (!linked || !row.tenantId) {
            return (
              <Typography
                variant="body2"
                color={theme.palette.warning.main}
                sx={{ fontStyle: "italic" }}
              >
                Not linked
              </Typography>
            );
          }
          return (
            <MuiLink
              component={RouterLink}
              to={`/o/${row.tenantId}/waste/manifests/${linked.id}`}
              variant="body2"
            >
              {linked.manifestTrackingNumber}
            </MuiLink>
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        flex: 0,
        minWidth: 96,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit manifest fields">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setReviewDocumentIds([row.id])}
                  aria-label="Edit manifest fields"
                  disabled={!row.sourceWasteManifest?.id}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="More actions">
              <IconButton
                size="small"
                onClick={(e) => {
                  setActionsAnchorEl(e.currentTarget);
                  setActionsAnchorRow(row);
                }}
                aria-label="More actions"
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [theme]
  );

  const defaultSearch = useMemo(() => buildManifestDocumentsSearch(seedTenantId), [seedTenantId]);

  return (
    // Standalone Observe tab: supply the page padding the tab panel doesn't
    // (px: 0). Matches the `py`/`px` of sibling tabs like Scout Nodes; the grid
    // keeps withPadding={false} so it doesn't double-pad horizontally.
    <Stack sx={{ py: 3, px: 3 }}>
      <Box sx={{ pb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Lists every RCRA manifest document across all tenants — including EPA printed-copy scans
          that have not been parsed yet — the source each was ingested from, and the manifest record
          each ended up linked to. Use this to spot orphaned uploads, documents that landed on the
          wrong manifest, or printed copies that still need parsing.{" "}
          <MuiLink component={RouterLink} to={MANIFEST_FAILED_JOBS_LINK}>
            View failed manifest ingest jobs
          </MuiLink>
          .
        </Typography>
      </Box>

      <OmnisearchDataGrid
        persistenceKey="waste-manifest-documents"
        isURLDriven
        columns={columns}
        dataQuery={MANIFEST_DOCUMENTS_QUERY}
        getItems={(data) => data.documents.items}
        getCount={(data) => data.documents.count}
        defaultSearch={defaultSearch}
        initialSortModel={[{ field: "createdAt", sort: "desc" }]}
        noDataMessage="No manifest documents found."
        rowHeight="auto"
        withPadding={false}
        // trackingNumber/extractedGeneratorId/linkedManifest/linkedFacility and the
        // lastExtractionAt date filter are wired to the resolver (ENG-15798).
        // shippedDate (not a doc-health signal) and actions (UI artifact) stay
        // suppressed — they remain dead auto-generated filters.
        excludeFilterColumns={["shippedDate", "actions"]}
      />

      <Menu anchorEl={actionsAnchorEl} open={Boolean(actionsAnchorEl)} onClose={closeActionsMenu}>
        {/* Parsing routes through a tenant-scoped extraction job, so a document
            with no tenant (surfaced by the cross-tenant view) can't be parsed
            from here — disable rather than queue a job with an empty tenant. */}
        <Tooltip
          title={
            actionsAnchorRow && !actionsAnchorRow.tenantId
              ? "This document has no tenant, so it can't be parsed here."
              : ""
          }
        >
          <span>
            <MenuItem
              disabled={!actionsAnchorRow?.tenantId}
              onClick={() => {
                const row = actionsAnchorRow;
                closeActionsMenu();
                if (row?.tenantId) setPendingReParseRow(row);
              }}
            >
              <ListItemIcon>
                <Refresh fontSize="small" />
              </ListItemIcon>
              <ListItemText>{reparseMenuLabel(actionsAnchorRow?.extractionStatus)}</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>

      {pendingReParseRow && (
        <ReParseDialog
          key={pendingReParseRow.id}
          row={pendingReParseRow}
          tenantId={pendingReParseRow.tenantId ?? ""}
          onClose={() => setPendingReParseRow(null)}
          onReadyForReview={setReviewDocumentIds}
        />
      )}

      {reviewDocumentIds && (
        <ManifestReviewDialog
          onClose={() => setReviewDocumentIds(null)}
          documentIds={reviewDocumentIds}
        />
      )}
    </Stack>
  );
}
